import { agentWorkflowUseCases } from './use-case-batches/agent-workflows'
import {
  validateUseCaseTemplates,
  type UseCaseTemplate,
  type UseCaseTemplateCategory,
} from './use-case-template-model'

export type {
  AgentWorkflowDetails,
  AgentWorkflowStep,
  UseCaseAccessMode,
  UseCaseTemplate,
  UseCaseTemplateCategory,
} from './use-case-template-model'
export {
  validateUseCaseRouteSlugs,
  validateUseCaseSkillSlugs,
  validateUseCaseTemplates,
} from './use-case-template-model'

export const useCaseTemplateCategories: {
  value: UseCaseTemplateCategory
  emoji: string
  blurb: string
}[] = [
  {
    value: 'Agent Workflows',
    emoji: '⌘',
    blurb:
      'Give Claude Code, Codex, Cursor, or another shell agent a concrete marketing job, the Soku CLI commands it needs, and an explicit safety boundary.',
  },
  {
    value: 'Campaign Execution',
    emoji: '🛠️',
    blurb:
      'Hands-on account work — Soku builds, edits, duplicates, and launches campaigns directly in your connected ad accounts.',
  },
  {
    value: 'Ads Audit & Optimization',
    emoji: '📊',
    blurb:
      'Soku reads your real performance data, finds what is leaking money, and proposes (or applies) the fix.',
  },
  {
    value: 'Competitive Intelligence',
    emoji: '🔎',
    blurb:
      'Soku researches competitors across ad libraries, search results, and their own sites, and reports what actually matters.',
  },
  {
    value: 'SEO & Content',
    emoji: '📈',
    blurb:
      'Keyword strategy, page plans, and content production grounded in your Search Console and market data.',
  },
  {
    value: 'Creative Generation',
    emoji: '🎨',
    blurb:
      'Soku produces ad images, videos, scripts, and copy variants — matched to your brand and each placement.',
  },
  {
    value: 'Analytics & Reporting',
    emoji: '📉',
    blurb:
      'Soku pulls the numbers across channels, explains what changed and why, and delivers reports people actually read.',
  },
  {
    value: 'Automation & Monitoring',
    emoji: '⏰',
    blurb:
      'Recurring jobs — Soku watches your accounts, competitors, and rankings on a schedule and alerts you when something moves.',
  },
  {
    value: 'Landing Pages & CRO',
    emoji: '🧪',
    blurb:
      'Soku builds and audits the pages your traffic lands on, so paid clicks and rankings turn into revenue.',
  },
]

const legacyUseCaseTemplates: UseCaseTemplate[] = [
  // ── Campaign Execution ────────────────────────────────────────────────
  {
    slug: 'duplicate-winning-campaign-new-creatives',
    title: 'Duplicate a Winning Campaign With New Creatives',
    description:
      'Copies your best-performing campaign, swaps in fresh creatives and copy, and keeps targeting and budget structure intact — ready to launch in minutes.',
    prompt:
      'Copy our best-performing Meta campaign from last month, keep the same audiences and budget split, but swap in the new product creatives and rewrite the primary text for a summer angle.',
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '📋',
  },
  {
    slug: 'launch-meta-campaign-from-brief',
    title: 'Launch a Meta Campaign From a One-Line Brief',
    description:
      'Turns a plain-language brief into a full campaign — objective, ad sets, audiences, placements, creatives, and tracking — then deploys it for your approval.',
    prompt:
      'Launch a Meta conversion campaign for our new skincare serum targeting US women 25-44, $100/day budget, ROAS goal 3:1. Draft the audiences, write the copy, and show me everything before it goes live.',
    category: 'Campaign Execution',
    tags: ['Founder', 'Meta Ads', 'DTC'],
    integrations: ['Meta Ads'],
    iconEmoji: '🚀',
  },
  {
    slug: 'fix-rejected-meta-ads',
    title: 'Diagnose and Fix Rejected Meta Ads',
    description:
      'Reads the rejection reason on each disapproved ad, edits the offending copy or creative to comply with policy, and resubmits for review.',
    prompt:
      'Three of our Meta ads got rejected this morning. Figure out why, fix the copy or creative so they comply, and resubmit them.',
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🩹',
  },
  {
    slug: 'bulk-edit-ad-copy-remove-claims',
    title: 'Bulk-Edit Copy Across Every Live Ad',
    description:
      'Sweeps every active ad for a phrase, claim, or price you need gone — then rewrites and updates them all in one pass instead of one-by-one edits.',
    prompt:
      'We can no longer mention the $49 price or the 3-day minimum stay in any ad. Find every live ad that mentions either, rewrite the copy without them, and push the updates.',
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads', 'Google Ads'],
    integrations: ['Meta Ads', 'Google Ads'],
    iconEmoji: '✂️',
  },
  {
    slug: 'create-custom-audience',
    title: 'Build a Custom Audience From Your Data',
    description:
      'Creates Meta custom audiences from site visitors, customer lists, or engagement — then builds lookalikes at the right percentages for prospecting.',
    prompt:
      "Create a custom audience of people who visited our pricing page in the last 30 days but didn't sign up, and build 1% and 3% lookalikes from our purchasers for prospecting.",
    category: 'Campaign Execution',
    tags: ['Growth', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '👥',
  },
  {
    slug: 'scale-adset-across-geos',
    title: 'Roll a Winning Ad Set Out to New Geos',
    description:
      'Duplicates a proven ad set into new countries or regions, adjusts budgets to market size, and localizes the copy where the language changes.',
    prompt:
      'Our US ad set is hitting target CPA. Duplicate it for the UK, Canada, and Australia with budgets scaled to audience size, and adjust any US-specific copy.',
    category: 'Campaign Execution',
    tags: ['Growth', 'Meta Ads', 'International'],
    integrations: ['Meta Ads'],
    iconEmoji: '🌍',
  },
  {
    slug: 'verify-pixel-and-events',
    title: 'Verify Pixel and Conversion Events',
    description:
      'Checks that your pixel fires on the right pages, events carry the right parameters, and conversions match what the ad platforms report.',
    prompt:
      'Check whether our Meta pixel and conversion events are firing correctly on the new checkout flow, and tell me if purchases are being double-counted or dropped.',
    category: 'Campaign Execution',
    tags: ['Ecommerce', 'Meta Ads'],
    integrations: ['Meta Ads', 'GA4'],
    iconEmoji: '🎯',
  },
  {
    slug: 'set-up-ab-test-campaigns',
    title: 'Set Up a Clean A/B Test',
    description:
      "Structures a proper split test — one variable, matched budgets, enough volume to reach significance — and tells you when there's a real winner.",
    prompt:
      "Set up an A/B test on Meta: same audience and budget, creative A vs creative B. Tell me how long it needs to run for significance and report the winner when it's decided.",
    category: 'Campaign Execution',
    tags: ['Growth', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '⚖️',
  },
  {
    slug: 'google-ads-negative-keywords-apply',
    title: 'Apply Negative Keywords From Wasted Spend',
    description:
      'Mines your search terms report for irrelevant queries burning budget, groups them into negative lists, and applies them at the right level.',
    prompt:
      "Go through our Google Ads search terms from the last 30 days, find the irrelevant queries we're paying for, and add them as negative keywords — grouped into shared lists where it makes sense.",
    category: 'Campaign Execution',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🚫',
  },
  {
    slug: 'adjust-budgets-by-roas',
    title: "Reallocate Budgets Toward What's Working",
    description:
      'Reviews spend and returns across campaigns, then shifts budget from underperformers to winners — with a clear before/after plan you approve first.',
    prompt:
      "Review all our active campaigns' ROAS for the last 14 days and propose a budget reallocation — move spend from anything below 1.5 ROAS to the winners. Show me the plan before applying.",
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads', 'Google Ads'],
    integrations: ['Meta Ads', 'Google Ads'],
    iconEmoji: '💸',
  },
  {
    slug: 'pause-fatigued-ads',
    title: 'Pause Fatigued Ads Before They Drag CPA',
    description:
      'Finds ads with rising frequency and falling CTR, pauses the ones past their prime, and queues replacements from your creative library.',
    prompt:
      'Find ads where frequency is above 4 and CTR has dropped more than 30% from their peak, pause them, and suggest which creatives from our library should replace them.',
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '😴',
  },
  {
    slug: 'launch-google-search-campaign',
    title: 'Launch a Google Search Campaign From Keywords',
    description:
      'Builds a search campaign from your seed keywords — ad groups by theme, responsive search ads, extensions, and conversion tracking wired up.',
    prompt:
      'Build a Google Search campaign for our plumbing service in Austin: tight ad groups from these seed keywords, responsive ads with call extensions, and conversion tracking on the booking form.',
    category: 'Campaign Execution',
    tags: ['Local business', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🔑',
  },
  {
    slug: 'sync-and-map-ad-accounts',
    title: 'Connect and Map Dozens of Client Accounts',
    description:
      'Identifies every ad account you have access to, maps them to the right client brands, and keeps data syncing so reporting stays current.',
    prompt:
      "Show me all the Meta ad accounts we're connected to, map each one to the right client brand, and flag any accounts that stopped syncing or lost permissions.",
    category: 'Campaign Execution',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads', 'Google Ads'],
    iconEmoji: '🔗',
  },
  {
    slug: 'schedule-dayparting',
    title: 'Set Up Dayparting From Conversion Patterns',
    description:
      'Analyzes when your conversions actually happen, then schedules ads (or bid adjustments) around the hours and days that convert.',
    prompt:
      'Look at when our Google Ads conversions actually happen by hour and day, and set up an ad schedule that cuts spend during the dead hours.',
    category: 'Campaign Execution',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🕐',
  },
  {
    slug: 'utm-cleanup-links',
    title: 'Standardize UTM Tags Across Every Ad',
    description:
      'Audits the tracking URLs on all live ads, fixes missing or inconsistent UTM parameters, and enforces one naming convention going forward.',
    prompt:
      'Audit the UTM parameters on every live ad across Meta and Google. Fix anything missing or inconsistent so our GA4 channel reporting is clean, and document the convention.',
    category: 'Campaign Execution',
    tags: ['Analytics', 'Meta Ads', 'Google Ads'],
    integrations: ['Meta Ads', 'Google Ads', 'GA4'],
    iconEmoji: '🏷️',
  },

  // ── Ads Audit & Optimization ──────────────────────────────────────────
  {
    slug: 'full-google-ads-account-audit',
    title: 'Full Google Ads Account Diagnostic',
    description:
      'Runs a structured audit across structure, keywords, quality scores, tracking, and budget pacing — and ranks the fixes by expected impact.',
    prompt:
      'Run a full diagnostic on our Google Ads account: campaign structure, keyword health, quality scores, conversion tracking, and budget pacing. Rank the fixes by impact.',
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🩺',
  },
  {
    slug: 'meta-account-audit',
    title: 'Meta Ads Account Audit',
    description:
      'Reviews account structure, audience overlap, creative fatigue, learning-phase churn, and attribution settings — with a prioritized action list.',
    prompt:
      'Audit our Meta Ads account: structure, audience overlap, creative fatigue, and learning phase resets. Give me the top 5 things to fix this week.',
    category: 'Ads Audit & Optimization',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🔬',
  },
  {
    slug: 'cpa-spike-investigation',
    title: 'Explain a Sudden CPA Spike',
    description:
      'Traces a cost spike to its actual cause — auction pressure, creative fatigue, tracking breakage, budget changes, or landing page issues — with evidence.',
    prompt:
      'Our CPA jumped 40% this week. Dig into the data and tell me exactly what changed — auction, creatives, tracking, budgets, or the landing page — and what to do about it.',
    category: 'Ads Audit & Optimization',
    tags: ['Growth', 'Meta Ads', 'Google Ads'],
    integrations: ['Meta Ads', 'Google Ads', 'GA4'],
    iconEmoji: '🚨',
  },
  {
    slug: 'search-terms-quality-audit',
    title: 'Search Terms Quality Audit',
    description:
      'Classifies every search term you paid for into converting, plausible, and junk — then quantifies the wasted spend and builds the negative list.',
    prompt:
      'Audit our Google Ads search terms for the last 30 days. How much are we spending on junk queries, which terms deserve their own ad groups, and what should be negatives?',
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🧹',
  },
  {
    slug: 'conversion-tracking-audit',
    title: 'Conversion Tracking Audit',
    description:
      'Cross-checks platform conversions against GA4 and your backend, finds double-counting, missing events, and misconfigured goals, and fixes the setup.',
    prompt:
      'Run a conversion tracking audit: compare what Google Ads and Meta report against GA4 and our actual signups. Find any double-counting or missing events and fix the configuration.',
    category: 'Ads Audit & Optimization',
    tags: ['Analytics', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '🧮',
  },
  {
    slug: 'quality-score-improvement',
    title: 'Quality Score Deep-Dive and Fix Plan',
    description:
      'Breaks down quality score by component — expected CTR, ad relevance, landing page experience — and produces per-ad-group fixes that lower CPCs.',
    prompt:
      'Our Google Ads quality scores are dragging. Break them down by component for each ad group and give me the specific ad copy and landing page fixes that will raise them.',
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '⭐',
  },
  {
    slug: 'device-bid-optimization',
    title: 'Fix Mobile vs Desktop Performance Gaps',
    description:
      'Compares CPA and conversion rate by device, finds where mobile is quietly bleeding budget, and sets bid adjustments or separate strategies.',
    prompt:
      "Our mobile CPA is 3x desktop. Break down performance by device, figure out whether it's the landing page or the traffic, and set the right device bid adjustments.",
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '📱',
  },
  {
    slug: 'monthly-paid-spend-review',
    title: 'Full-Month Paid Spend Review',
    description:
      "Reviews everything you spent across platforms last month — what drove results, what didn't, and where next month's budget should go instead.",
    prompt:
      'Do a full-month review of all our paid spend across Google and Meta: total spend, what actually drove conversions, what underperformed, and your budget recommendation for next month.',
    category: 'Ads Audit & Optimization',
    tags: ['Agency', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '📆',
  },
  {
    slug: 'pmax-audit',
    title: 'Performance Max Campaign Audit',
    description:
      'Opens the PMax black box — asset group performance, search themes, channel distribution, and cannibalization of your brand terms.',
    prompt:
      'Audit our Performance Max campaigns: which asset groups work, where the spend actually goes by channel, and whether PMax is cannibalizing our brand search terms.',
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '📦',
  },
  {
    slug: 'creative-performance-audit',
    title: 'Creative Performance Audit',
    description:
      'Ranks every creative by hook rate, hold rate, CTR, and CPA — then tells you which angles, formats, and hooks to double down on.',
    prompt:
      'Analyze all our Meta creatives from the last 60 days: rank them by hook rate, CTR, and CPA, and tell me which angles and formats we should make more of.',
    category: 'Ads Audit & Optimization',
    tags: ['Creative strategist', 'Meta Ads', 'TikTok Ads'],
    integrations: ['Meta Ads', 'TikTok Ads'],
    iconEmoji: '🎬',
  },
  {
    slug: 'audience-overlap-frequency-audit',
    title: 'Audience Overlap and Frequency Audit',
    description:
      'Detects where your ad sets bid against each other and where frequency is burning out audiences — then proposes a consolidated structure.',
    prompt:
      'Check our Meta ad sets for audience overlap and excessive frequency. Are we bidding against ourselves anywhere? Propose a consolidated structure.',
    category: 'Ads Audit & Optimization',
    tags: ['Agency', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🔄',
  },
  {
    slug: 'keyword-expansion-from-converters',
    title: 'Expand Keywords From What Already Converts',
    description:
      'Finds new keyword opportunities hiding in your converting search terms, checks volume and competition, and builds them into your structure.',
    prompt:
      "Find new keywords from our converting search terms in the last 90 days — things we're matching on but haven't broken out. Check volumes and add the good ones to the right ad groups.",
    category: 'Ads Audit & Optimization',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🌱',
  },
  {
    slug: 'geo-performance-breakdown',
    title: 'Geo Performance Breakdown and Reallocation',
    description:
      "Breaks results down by country, region, and city — finds where you're profitable and where you're subsidizing bad markets, then rebalances.",
    prompt:
      'Break down our ad performance by geography. Which countries and cities are actually profitable? Exclude or down-bid the money losers and shift budget to the winners.',
    category: 'Ads Audit & Optimization',
    tags: ['Growth', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🗺️',
  },
  {
    slug: 'message-match-audit',
    title: 'Ad-to-Landing-Page Message Match Audit',
    description:
      "Compares each ad's promise to the page it lands on, scores the match, and flags mismatches that inflate your cost per conversion.",
    prompt:
      'For our top 20 ads by spend, compare the ad copy against the landing page each one sends traffic to. Score the message match and flag the mismatches killing conversion.',
    category: 'Ads Audit & Optimization',
    tags: ['CRO', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🪞',
  },
  {
    slug: 'wasted-spend-report',
    title: 'Find All the Wasted Spend in One Sweep',
    description:
      'One pass across every account: junk search terms, dead placements, overlapping audiences, fatigued ads, and broken tracking — totaled in dollars.',
    prompt:
      'Sweep all our ad accounts and total up the wasted spend: junk queries, dead placements, overlapping audiences, fatigued creatives. Give me a dollar figure and the fix list.',
    category: 'Ads Audit & Optimization',
    tags: ['Founder', 'Google Ads', 'Meta Ads'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🕳️',
  },

  // ── Competitive Intelligence ──────────────────────────────────────────
  {
    slug: 'competitor-meta-ads-spy',
    title: "Spy on a Competitor's Meta Ads",
    description:
      "Pulls a competitor's active ads from the Meta Ad Library, identifies their longest-running (likely winning) creatives, and decodes their strategy.",
    prompt:
      'Pull everything [competitor] is running in the Meta Ad Library. Which ads have been live longest, what angles and offers are they pushing, and what should we test in response?',
    category: 'Competitive Intelligence',
    tags: ['Growth', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🕵️',
  },
  {
    slug: 'competitor-google-ads-analysis',
    title: "Analyze a Competitor's Google Ads Strategy",
    description:
      "Maps the keywords a competitor bids on, their ad copy patterns, and estimated spend — and finds the terms they're winning that you're not on.",
    prompt:
      "Analyze [competitor]'s Google Ads presence: what keywords are they bidding on, what does their copy promise, and which of their profitable terms are we missing?",
    category: 'Competitive Intelligence',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🔦',
  },
  {
    slug: 'full-competitive-brief',
    title: 'Full Competitive Brief on One Rival',
    description:
      'A complete dossier: product and pricing, positioning, paid channels, SEO footprint, and social presence — condensed into a brief your team can act on.',
    prompt:
      'Build a full competitive brief on [competitor]: product, pricing, positioning, where they advertise, their SEO strength, and their social presence. End with the 3 biggest openings for us.',
    category: 'Competitive Intelligence',
    tags: ['Founder', 'Strategy'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '📁',
  },
  {
    slug: 'tiktok-creative-spy',
    title: 'Decode Competitor TikTok Creative Strategy',
    description:
      "Finds competitors' top TikTok ads, analyzes hooks, pacing, and formats frame-by-frame, and turns the patterns into briefs for your own creatives.",
    prompt:
      "Find [competitor]'s best-performing TikTok ads, break down their hooks and formats, and write 3 creative briefs that beat them at their own pattern.",
    category: 'Competitive Intelligence',
    tags: ['Creative strategist', 'TikTok Ads'],
    integrations: ['TikTok Ads'],
    iconEmoji: '📹',
  },
  {
    slug: 'seo-keyword-gap',
    title: 'SEO Keyword Gap vs Competitors',
    description:
      "Compares your ranking keywords against 2-3 competitors, finds the terms they rank for and you don't, and prioritizes by value and difficulty.",
    prompt:
      "Compare our ranking keywords against [competitor A] and [competitor B]. Where do they rank that we don't? Prioritize the gaps by traffic value and difficulty.",
    category: 'Competitive Intelligence',
    tags: ['SEO'],
    integrations: ['Google Search Console'],
    iconEmoji: '📐',
  },
  {
    slug: 'competitor-landing-page-teardown',
    title: 'Competitor Landing Page Teardown',
    description:
      "Dissects a competitor's landing pages — offer, structure, social proof, CTAs — and extracts what to steal and what to differentiate against.",
    prompt:
      "Tear down [competitor]'s main landing pages: their offer, page structure, social proof, and CTAs. What's working that we should adopt, and where can we differentiate?",
    category: 'Competitive Intelligence',
    tags: ['CRO', 'Strategy'],
    integrations: [],
    iconEmoji: '🔨',
  },
  {
    slug: 'positioning-comparison',
    title: 'Positioning and Messaging Comparison',
    description:
      'Maps how every player in your category describes themselves, finds the crowded claims and the empty space, and drafts positioning you can own.',
    prompt:
      'Map the positioning of the top 6 players in our category — taglines, key claims, target customers. Where is everyone saying the same thing, and what position is open for us?',
    category: 'Competitive Intelligence',
    tags: ['Founder', 'Strategy', 'B2B SaaS'],
    integrations: [],
    iconEmoji: '🧭',
  },
  {
    slug: 'new-competitor-discovery',
    title: "Discover Competitors You Don't Know About",
    description:
      'Searches your keywords, ad auctions, and review sites to surface the competitors actually taking your customers — not just the ones you watch.',
    prompt:
      "Who are we actually competing with? Search our top keywords, check who bids against us in ad auctions, and scan review sites. List anyone taking our customers that we're not tracking.",
    category: 'Competitive Intelligence',
    tags: ['Founder', 'Strategy'],
    integrations: ['Google Ads'],
    iconEmoji: '🆕',
  },
  {
    slug: 'ai-search-visibility-check',
    title: 'Check Your Visibility in AI Search',
    description:
      'Tests how ChatGPT, Perplexity, and Google AI Overviews answer your category questions — who gets recommended, who gets ignored, and why.',
    prompt:
      'When people ask ChatGPT and Perplexity for tools in our category, who gets recommended? Check our visibility in AI answers vs competitors and tell me how to improve it.',
    category: 'Competitive Intelligence',
    tags: ['SEO', 'GEO'],
    integrations: [],
    iconEmoji: '🤖',
  },
  {
    slug: 'competitor-pricing-watch',
    title: 'Track Competitor Pricing and Packaging Changes',
    description:
      'Snapshots competitor pricing pages and plans, compares against your own, and explains what their packaging says about their strategy.',
    prompt:
      "Compare our pricing and packaging against [competitor A], [B], and [C]. Where are we mispriced, and what does their plan structure tell us about who they're targeting?",
    category: 'Competitive Intelligence',
    tags: ['Founder', 'B2B SaaS'],
    integrations: [],
    iconEmoji: '💲',
  },
  {
    slug: 'serp-ads-monitoring',
    title: "See Who's Bidding on Your Brand",
    description:
      'Checks the ads showing on your brand-name searches, identifies which competitors are poaching your traffic, and drafts the defense plan.',
    prompt:
      "Search our brand name and our top product terms — who's running ads against them? Show me the competitor ads targeting our brand and give me a defense plan.",
    category: 'Competitive Intelligence',
    tags: ['PPC', 'Google Ads'],
    integrations: ['Google Ads'],
    iconEmoji: '🛡️',
  },
  {
    slug: 'linkedin-competitor-content',
    title: "Analyze Competitors' LinkedIn Playbook",
    description:
      'Studies what your competitors post on LinkedIn, which posts get traction, and their cadence — then drafts a content plan that outperforms it.',
    prompt:
      'Analyze the LinkedIn presence of [competitor] and their founders: what do they post, what gets engagement, how often? Draft us a 2-week plan that beats their playbook.',
    category: 'Competitive Intelligence',
    tags: ['B2B SaaS', 'LinkedIn'],
    integrations: ['LinkedIn'],
    iconEmoji: '💼',
  },
  {
    slug: 'app-store-competitor-analysis',
    title: 'App Store Competitor Analysis',
    description:
      "Reviews competitors' app store listings, rankings, review themes, and update cadence — and finds the complaints you can win users on.",
    prompt:
      'Analyze the top 5 apps competing with ours: their store listings, rankings, and what users complain about in reviews. Which recurring complaints could we win users on?',
    category: 'Competitive Intelligence',
    tags: ['Mobile', 'App marketing'],
    integrations: [],
    iconEmoji: '📲',
  },
  {
    slug: 'share-of-voice-report',
    title: 'Share of Voice Across Search and Social',
    description:
      "Measures how much of your category's search results, ads, and social conversation each player owns — and tracks how it shifts over time.",
    prompt:
      'Measure share of voice in our category: who owns the search results, the ad auctions, and the social conversation? Set a baseline we can track monthly.',
    category: 'Competitive Intelligence',
    tags: ['Strategy', 'Brand'],
    integrations: ['Google Ads'],
    iconEmoji: '📢',
  },
  {
    slug: 'win-loss-battlecard',
    title: 'Build Sales Battlecards From Competitive Research',
    description:
      'Turns competitive research into battlecards your sales team can use — objection handling, trap-setting questions, and honest weak spots.',
    prompt:
      "Build a battlecard for selling against [competitor]: their real strengths and weaknesses, how to handle 'we're also looking at them', and trap-setting questions our reps can ask.",
    category: 'Competitive Intelligence',
    tags: ['Sales', 'B2B SaaS'],
    integrations: [],
    iconEmoji: '🃏',
  },

  // ── SEO & Content ─────────────────────────────────────────────────────
  {
    slug: 'icp-pages-strategy',
    title: 'Plan and Draft ICP Landing Pages',
    description:
      'Splits your audience into buyer vs user (or by vertical), plans a page for each ICP with the right search intent, and drafts them all.',
    prompt:
      'We want to test our ICP split — buyers vs users. What pages would you create for each? Plan the set, then draft all 8 pages with titles, meta, and body copy.',
    category: 'SEO & Content',
    tags: ['B2B SaaS', 'SEO'],
    integrations: ['Google Search Console'],
    iconEmoji: '🎯',
  },
  {
    slug: 'keyword-research-content-plan',
    title: 'Keyword Research Into a 90-Day Content Plan',
    description:
      'Researches keywords by intent and difficulty, clusters them into topics, and sequences a 90-day publishing plan weighted toward quick wins.',
    prompt:
      'Do keyword research for our product category, cluster by intent, and build a 90-day content calendar — quick-win pages first, pillar content after.',
    category: 'SEO & Content',
    tags: ['SEO', 'Content'],
    integrations: ['Google Search Console'],
    iconEmoji: '🗓️',
  },
  {
    slug: 'gsc-quick-wins',
    title: 'Find Quick Wins in Search Console',
    description:
      "Mines your real Search Console data for page-2 keywords, low-CTR page-1 rankings, and titles that don't match the queries — then fixes them.",
    prompt:
      "Pull our Search Console data and find the quick wins: keywords ranking 5-15, page-1 results with bad CTR, and titles that don't match what people actually search. Fix the on-page issues.",
    category: 'SEO & Content',
    tags: ['SEO'],
    integrations: ['Google Search Console'],
    iconEmoji: '⚡',
  },
  {
    slug: 'technical-seo-audit',
    title: 'Technical SEO Audit',
    description:
      'Crawls your site for indexing problems, broken links, redirect chains, sitemap issues, and Core Web Vitals — prioritized by traffic at risk.',
    prompt:
      'Run a technical SEO audit on our site: indexing, sitemaps, redirects, broken links, and page speed. Prioritize by how much traffic each issue puts at risk.',
    category: 'SEO & Content',
    tags: ['SEO', 'Technical'],
    integrations: ['Google Search Console'],
    iconEmoji: '🔧',
  },
  {
    slug: 'content-refresh-decay',
    title: 'Refresh Decaying Content',
    description:
      'Finds pages losing rankings and traffic, diagnoses why — staleness, intent shift, stronger competitors — and rewrites them to recover.',
    prompt:
      'Which of our pages are losing traffic? Diagnose why each one is decaying and refresh the top 5 — updated data, better intent match, stronger internal links.',
    category: 'SEO & Content',
    tags: ['SEO', 'Content'],
    integrations: ['Google Search Console'],
    iconEmoji: '🔁',
  },
  {
    slug: 'programmatic-seo-pages',
    title: 'Design a Programmatic SEO Play',
    description:
      'Finds a repeatable page pattern in your space — integrations, comparisons, templates, locations — and designs the data model and page template to scale it.',
    prompt:
      'Design a programmatic SEO play for us: what repeatable page pattern (comparisons, integrations, use cases, locations) fits our product, and what would the template and data look like?',
    category: 'SEO & Content',
    tags: ['SEO', 'Growth'],
    integrations: [],
    iconEmoji: '🏭',
  },
  {
    slug: 'internal-linking-optimization',
    title: 'Fix Internal Linking Structure',
    description:
      'Maps how authority flows through your site, finds orphan pages and wasted link equity, and adds the internal links that lift target pages.',
    prompt:
      'Map our internal linking: find orphan pages and money pages starved of links. Add contextual internal links from our strongest pages to the ones we need ranking.',
    category: 'SEO & Content',
    tags: ['SEO', 'Technical'],
    integrations: ['Google Search Console'],
    iconEmoji: '🕸️',
  },
  {
    slug: 'blog-post-production',
    title: 'Research and Write a Ranking Blog Post',
    description:
      'Reverse-engineers what ranks for your target keyword, writes a post that covers the gaps competitors miss, and formats it to win the SERP.',
    prompt:
      'Write a blog post targeting [keyword]. Study what currently ranks, cover what they miss, include original angles from our product experience, and format it to compete for the featured snippet.',
    category: 'SEO & Content',
    tags: ['Content', 'SEO'],
    integrations: [],
    iconEmoji: '✍️',
  },
  {
    slug: 'aeo-faq-optimization',
    title: 'Optimize Content for AI Answers (AEO)',
    description:
      'Restructures key pages with direct answers, FAQ schema, and citable claims so ChatGPT, Perplexity, and AI Overviews quote you.',
    prompt:
      'Optimize our top 10 pages for AI search: add direct answer blocks, FAQ schema, and citable stats so ChatGPT and Perplexity start referencing us for category questions.',
    category: 'SEO & Content',
    tags: ['SEO', 'GEO'],
    integrations: [],
    iconEmoji: '💬',
  },
  {
    slug: 'comparison-pages',
    title: "Build 'X vs Y' Comparison Pages",
    description:
      'Creates honest comparison pages against each competitor — feature tables, pricing, who each is right for — targeting high-intent comparison searches.',
    prompt:
      "Build comparison pages for us vs our top 4 competitors: honest feature tables, pricing comparison, and 'who should choose which'. Target the '[us] vs [them]' searches.",
    category: 'SEO & Content',
    tags: ['SEO', 'B2B SaaS'],
    integrations: [],
    iconEmoji: '🆚',
  },
  {
    slug: 'content-localization',
    title: 'Localize Content for New Markets',
    description:
      'Adapts your best-performing pages for new languages and markets — real localization with local keyword research, not word-for-word translation.',
    prompt:
      'Localize our top 10 pages for the German market: research what Germans actually search for in our category, adapt the messaging, and set up hreflang correctly.',
    category: 'SEO & Content',
    tags: ['International', 'SEO'],
    integrations: ['Google Search Console'],
    iconEmoji: '🌐',
  },
  {
    slug: 'backlink-profile-analysis',
    title: 'Backlink Profile Analysis and Link Plan',
    description:
      'Audits your backlink profile against competitors, finds who links to them but not you, and builds a realistic link acquisition list.',
    prompt:
      'Analyze our backlink profile vs [competitor]. Who links to them but not us? Build a prioritized outreach list of realistic link targets with the angle for each.',
    category: 'SEO & Content',
    tags: ['SEO', 'Link building'],
    integrations: [],
    iconEmoji: '🔗',
  },
  {
    slug: 'listicle-placement-strategy',
    title: "Get Included in 'Best X' Listicles",
    description:
      "Finds the listicles ranking for your category's 'best' searches, identifies which you're missing from, and drafts the pitches to get included.",
    prompt:
      "Find every 'best [category] tools' listicle ranking on page 1. Which ones are we missing from? Draft outreach pitches to get us included in the top 10.",
    category: 'SEO & Content',
    tags: ['SEO', 'PR'],
    integrations: [],
    iconEmoji: '🏆',
  },
  {
    slug: 'seo-content-brief-generator',
    title: 'Generate Writer-Ready Content Briefs',
    description:
      'Turns target keywords into complete briefs — search intent, required subtopics, entities to cover, competitor gaps, and internal links to include.',
    prompt:
      'Create content briefs for these 5 target keywords: intent analysis, required subtopics, questions to answer, competitor gaps, and which internal links to include.',
    category: 'SEO & Content',
    tags: ['Content', 'SEO'],
    integrations: [],
    iconEmoji: '📋',
  },
  {
    slug: 'local-seo-optimization',
    title: 'Local SEO for Service Businesses',
    description:
      'Optimizes your Google Business Profile, builds location pages, and structures local citations so you win the map pack in your service areas.',
    prompt:
      "Optimize our local SEO: audit our Google Business Profile, build pages for each service area, and tell me what's keeping us out of the map pack.",
    category: 'SEO & Content',
    tags: ['Local business', 'SEO'],
    integrations: [],
    iconEmoji: '📍',
  },

  // ── Creative Generation ───────────────────────────────────────────────
  {
    slug: 'product-photo-to-video-ad',
    title: 'Turn a Product Photo Into a Video Ad',
    description:
      'Takes a single product photo and produces a scroll-stopping video ad — scenes, motion, text overlays, and music — sized for every placement.',
    prompt:
      "Here's a photo of our product. Turn it into a 15-second video ad for Meta and TikTok — show it in use, add a hook in the first 2 seconds, and end with our discount code.",
    category: 'Creative Generation',
    tags: ['Ecommerce', 'Video', 'Meta Ads', 'TikTok Ads'],
    integrations: ['Meta Ads', 'TikTok Ads'],
    iconEmoji: '🎥',
  },
  {
    slug: 'ad-variants-from-winner',
    title: 'Generate New Variants From Your Winning Ad',
    description:
      'Deconstructs why your best ad works — hook, angle, format — and produces fresh variants that keep the winning elements while fighting fatigue.',
    prompt:
      'Our top ad is fatiguing. Analyze why it worked and generate 3 new variants: same angle with new visuals, new angle with the same format, and one wildcard.',
    category: 'Creative Generation',
    tags: ['Creative strategist', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🧬',
  },
  {
    slug: 'ugc-script-batch',
    title: 'Write a Batch of UGC Scripts',
    description:
      'Writes creator-ready UGC scripts — hooks, beats, b-roll notes, and CTAs — across proven frameworks like problem-agitate-solve and 3 reasons why.',
    prompt:
      'Write 10 UGC scripts for our product across different frameworks: problem-agitate-solve, 3 reasons why, day-in-the-life, and unboxing. Include hook lines and b-roll notes for creators.',
    category: 'Creative Generation',
    tags: ['DTC', 'UGC', 'TikTok Ads'],
    integrations: [],
    iconEmoji: '🎤',
  },
  {
    slug: 'resize-adapt-creatives-placements',
    title: 'Adapt One Creative to Every Placement',
    description:
      'Takes your hero creative and produces correctly-sized, natively-formatted versions for Feed, Stories, Reels, TikTok, Shorts, and display.',
    prompt:
      'Take this hero creative and adapt it for every placement: square feed, 9:16 Stories and Reels, TikTok with native-style captions, and the standard display sizes.',
    category: 'Creative Generation',
    tags: ['Designer', 'Meta Ads', 'TikTok Ads'],
    integrations: ['Meta Ads', 'TikTok Ads'],
    iconEmoji: '📐',
  },
  {
    slug: 'localized-ad-copy',
    title: 'Localize Ad Copy for New Markets',
    description:
      'Rewrites your ads for new languages and cultures — local idioms, offers, and social proof — not machine translation of English copy.',
    prompt:
      'Localize our 5 best-performing ads for the Saudi market: Arabic copy that actually sounds native, culturally right visuals notes, and offers that fit local buying habits.',
    category: 'Creative Generation',
    tags: ['International', 'Ecommerce'],
    integrations: ['Meta Ads'],
    iconEmoji: '🗣️',
  },
  {
    slug: 'hook-library',
    title: 'Build a Hook Library for Your Product',
    description:
      'Generates dozens of scroll-stopping hooks across curiosity, controversy, social proof, and outcome archetypes — ranked by fit to your audience.',
    prompt:
      'Build us a hook library: 50 first-lines for ads and videos across curiosity, controversy, social proof, and bold-outcome styles. Rank the top 10 for our audience.',
    category: 'Creative Generation',
    tags: ['Creative strategist', 'Copywriting'],
    integrations: [],
    iconEmoji: '🪝',
  },
  {
    slug: 'static-ads-from-landing-page',
    title: 'Generate Static Ads From Your Landing Page',
    description:
      'Reads your landing page, extracts the strongest claims and proof points, and designs on-brand static ads that pre-sell the click.',
    prompt:
      'Read our landing page and generate 6 static ad concepts from it — pull the strongest claims and testimonials, keep our brand colors and fonts, and write matching headlines.',
    category: 'Creative Generation',
    tags: ['Designer', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🖼️',
  },
  {
    slug: 'seasonal-campaign-creatives',
    title: 'Produce a Seasonal Campaign Creative Set',
    description:
      'Builds a complete creative set for a seasonal moment — Black Friday, back-to-school, holidays — with a consistent concept across every format.',
    prompt:
      'Build our Black Friday creative set: one strong concept carried across video, statics, Stories, and email headers, with countdown and last-chance variants ready to schedule.',
    category: 'Creative Generation',
    tags: ['Ecommerce', 'DTC'],
    integrations: ['Meta Ads'],
    iconEmoji: '🎁',
  },
  {
    slug: 'testimonial-ads-from-reviews',
    title: 'Turn Customer Reviews Into Testimonial Ads',
    description:
      'Mines your reviews for the most persuasive quotes, matches them to objections they resolve, and designs testimonial ads around them.',
    prompt:
      'Go through our reviews, pull the 10 most persuasive quotes, match each to the objection it kills, and design testimonial ads around the best 5.',
    category: 'Creative Generation',
    tags: ['DTC', 'Social proof'],
    integrations: [],
    iconEmoji: '⭐',
  },
  {
    slug: 'carousel-ads-product-line',
    title: 'Design Carousel Ads for a Product Line',
    description:
      'Structures carousels that actually get swiped — story-driven card sequences, per-card copy, and product lineups ordered by margin or popularity.',
    prompt:
      'Design 3 carousel ads for our product line: one story-driven sequence, one bestseller lineup, one before/after. Write the per-card copy and headline for each.',
    category: 'Creative Generation',
    tags: ['Ecommerce', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🎠',
  },
  {
    slug: 'brand-consistent-image-generation',
    title: 'Generate On-Brand Images at Scale',
    description:
      "Learns your brand's colors, typography, and visual style from your site and assets, then generates campaign images that look like you made them.",
    prompt:
      'Learn our brand style from our website and these assets, then generate 12 campaign images for our new feature launch that look unmistakably like our brand.',
    category: 'Creative Generation',
    tags: ['Designer', 'Brand'],
    integrations: [],
    iconEmoji: '🎨',
  },
  {
    slug: 'video-ad-recuts',
    title: 'Recut a Long Video Into Ad-Length Clips',
    description:
      'Slices a webinar, demo, or founder video into punchy ad-length cuts — finds the strongest moments, adds captions, and formats per platform.',
    prompt:
      "Here's our 20-minute product demo. Cut it into 5 ad-length clips around the strongest moments, add captions, and format them for Reels and TikTok.",
    category: 'Creative Generation',
    tags: ['Video', 'B2B SaaS'],
    integrations: [],
    iconEmoji: '✂️',
  },
  {
    slug: 'offer-angle-testing-matrix',
    title: 'Build an Offer and Angle Testing Matrix',
    description:
      'Maps your possible offers against audience pain points into a testing matrix, then generates the ad copy for each cell worth testing.',
    prompt:
      'Build an offer/angle testing matrix: our 3 possible offers against the top 4 customer pain points. Write ad copy for the 6 cells most worth testing first.',
    category: 'Creative Generation',
    tags: ['Growth', 'Copywriting'],
    integrations: [],
    iconEmoji: '🧩',
  },
  {
    slug: 'linkedin-thought-leadership-posts',
    title: 'Draft a Month of LinkedIn Posts',
    description:
      'Turns your expertise and customer stories into a month of LinkedIn posts in your voice — hooks, formatting, and a posting cadence that compounds.',
    prompt:
      'Draft 12 LinkedIn posts for our founder: customer stories, contrarian takes on our industry, and behind-the-scenes lessons. Match this voice sample and schedule 3 per week.',
    category: 'Creative Generation',
    tags: ['B2B SaaS', 'LinkedIn', 'Founder'],
    integrations: ['LinkedIn'],
    iconEmoji: '📝',
  },
  {
    slug: 'email-campaign-sequence',
    title: 'Write a Revenue Email Sequence',
    description:
      'Writes a complete email sequence — welcome, nurture, or winback — with subject lines tested against each other and CTAs matched to lifecycle stage.',
    prompt:
      'Write our post-purchase email sequence: 5 emails from order confirmation to review request to cross-sell. Two subject line options each, in our brand voice.',
    category: 'Creative Generation',
    tags: ['Ecommerce', 'Email', 'Retention'],
    integrations: [],
    iconEmoji: '📧',
  },

  // ── Analytics & Reporting ─────────────────────────────────────────────
  {
    slug: 'monday-morning-performance-report',
    title: 'Monday Morning Performance Report',
    description:
      "Every Monday, pulls last week's performance across all channels, compares to the previous week and targets, and explains what changed in plain English.",
    prompt:
      "Every Monday morning, pull last week's performance across Google, Meta, and GA4 — spend, conversions, CPA, ROAS vs the week before — and send me a summary that explains what changed and why.",
    category: 'Analytics & Reporting',
    tags: ['Agency', 'Reporting'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '☕',
  },
  {
    slug: 'ga4-funnel-analysis',
    title: 'GA4 Funnel Drop-Off Analysis',
    description:
      'Builds your conversion funnel from GA4 events, finds the biggest drop-off step, and segments it by source, device, and geography to locate the leak.',
    prompt:
      "Analyze our signup funnel in GA4: where's the biggest drop-off? Break it down by traffic source, device, and country so we know exactly which segment is leaking.",
    category: 'Analytics & Reporting',
    tags: ['Analytics', 'Product marketing'],
    integrations: ['GA4'],
    iconEmoji: '🔻',
  },
  {
    slug: 'cross-channel-dashboard',
    title: 'One Report Across Every Channel',
    description:
      'Merges Google, Meta, TikTok, and organic into one view with consistent metrics — no more tab-hopping between four dashboards that disagree.',
    prompt:
      "Build me one weekly view across Google Ads, Meta, TikTok, and organic: consistent spend, CAC, and ROAS definitions, with each channel's trend and share of new customers.",
    category: 'Analytics & Reporting',
    tags: ['Growth', 'Reporting'],
    integrations: ['Google Ads', 'Meta Ads', 'TikTok Ads', 'GA4'],
    iconEmoji: '🖥️',
  },
  {
    slug: 'attribution-comparison',
    title: 'Compare Attribution Models Honestly',
    description:
      'Shows how your channel performance shifts across last-click, data-driven, and platform-reported attribution — and what to actually trust for budgeting.',
    prompt:
      'Compare our channel performance under last-click vs data-driven attribution vs what the platforms self-report. Where do they disagree most, and what should we trust for budget decisions?',
    category: 'Analytics & Reporting',
    tags: ['Analytics', 'Strategy'],
    integrations: ['GA4', 'Google Ads', 'Meta Ads'],
    iconEmoji: '🧭',
  },
  {
    slug: 'client-ready-monthly-report',
    title: 'Client-Ready Monthly Report',
    description:
      "Produces a polished monthly report for each client — results vs goals, what was done, insights, and next month's plan — formatted to forward as-is.",
    prompt:
      "Generate this month's client report for [client]: performance vs their goals, the work we shipped, three insights they'll care about, and next month's plan. Make it presentable enough to send directly.",
    category: 'Analytics & Reporting',
    tags: ['Agency', 'Reporting'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '📄',
  },
  {
    slug: 'anomaly-explanation',
    title: 'Explain Any Metric Anomaly',
    description:
      'Point at any weird number — traffic dip, conversion spike, CTR collapse — and Soku traces it through your data to the actual cause.',
    prompt:
      'Our organic traffic dropped 25% on Tuesday. Trace it: which pages, which queries, which geography — and was it an algorithm update, tracking issue, or something we changed?',
    category: 'Analytics & Reporting',
    tags: ['Analytics', 'SEO'],
    integrations: ['GA4', 'Google Search Console'],
    iconEmoji: '❓',
  },
  {
    slug: 'ltv-cohort-analysis',
    title: 'LTV and Cohort Analysis by Channel',
    description:
      'Analyzes customer value by acquisition cohort and channel — revealing which channels bring customers who stay and which bring one-and-done buyers.',
    prompt:
      'Run a cohort analysis: LTV at 30/90/180 days by acquisition channel. Which channels bring customers who actually stick around, and how should that change our CAC targets?',
    category: 'Analytics & Reporting',
    tags: ['Growth', 'Retention'],
    integrations: ['GA4'],
    iconEmoji: '📊',
  },
  {
    slug: 'creative-performance-matrix',
    title: 'Creative Performance Matrix',
    description:
      'Crosses creative attributes — format, hook type, length, angle — against performance to reveal which combinations actually drive your results.',
    prompt:
      'Build a creative performance matrix from our last 90 days: format × hook type × length against CPA and hook rate. Which combinations should the next creative batch use?',
    category: 'Analytics & Reporting',
    tags: ['Creative strategist', 'Meta Ads'],
    integrations: ['Meta Ads'],
    iconEmoji: '🎛️',
  },
  {
    slug: 'lead-quality-report',
    title: 'Lead Quality by Source Report',
    description:
      'Follows leads from first click to closed deal, scores quality by source and campaign, and reallocates toward what produces revenue, not just form fills.',
    prompt:
      'Analyze lead quality by source: which campaigns produce leads that actually close vs just form fills? Score each source and tell me where to shift budget.',
    category: 'Analytics & Reporting',
    tags: ['B2B SaaS', 'Sales'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🎖️',
  },
  {
    slug: 'budget-scenario-planning',
    title: 'Budget Scenario Planning',
    description:
      'Models what happens at different budget levels using your actual marginal performance — where returns diminish and where headroom remains.',
    prompt:
      'If we increased ad spend 50%, where should it go? Model scenarios from our actual data: which campaigns have headroom and which are already hitting diminishing returns.',
    category: 'Analytics & Reporting',
    tags: ['Founder', 'Strategy'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🎲',
  },
  {
    slug: 'board-ready-growth-digest',
    title: 'Board-Ready Growth Digest',
    description:
      "Condenses the month's marketing performance into the narrative your board actually wants — growth drivers, efficiency trends, and honest risks.",
    prompt:
      "Prepare the marketing section for our board deck: this month's growth drivers, CAC and efficiency trends, what's working, what's at risk, and what we're doing about it.",
    category: 'Analytics & Reporting',
    tags: ['Founder', 'Reporting'],
    integrations: ['Google Ads', 'Meta Ads', 'GA4'],
    iconEmoji: '🏛️',
  },
  {
    slug: 'product-analytics-funnel',
    title: 'Connect Marketing Spend to Product Behavior',
    description:
      'Joins ad platform data with product analytics to show which campaigns drive users who activate and retain — not just users who sign up.',
    prompt:
      'Connect our ad data to PostHog: which campaigns drive users who actually activate and are still active at day 30? Rank campaigns by activated-user CAC, not signup CAC.',
    category: 'Analytics & Reporting',
    tags: ['Product marketing', 'B2B SaaS'],
    integrations: ['PostHog', 'Google Ads', 'Meta Ads'],
    iconEmoji: '🔬',
  },
  {
    slug: 'seo-performance-report',
    title: 'Monthly SEO Performance Report',
    description:
      "Reports rankings, clicks, and conversions from organic — which pages gained or lost, which queries moved, and what next month's priorities are.",
    prompt:
      'Build our monthly SEO report: clicks and impressions trends, biggest ranking gains and losses, pages that need attention, and the top 3 priorities for next month.',
    category: 'Analytics & Reporting',
    tags: ['SEO', 'Reporting'],
    integrations: ['Google Search Console', 'GA4'],
    iconEmoji: '📈',
  },
  {
    slug: 'promo-campaign-postmortem',
    title: 'Campaign Postmortem Analysis',
    description:
      'After a launch or promo, reconstructs what actually happened — incremental lift vs cannibalization, channel contribution, and lessons for next time.',
    prompt:
      'Do a postmortem on our spring sale: real incremental revenue vs pulled-forward purchases, which channels drove it, what the discount cost us, and what to do differently next time.',
    category: 'Analytics & Reporting',
    tags: ['Ecommerce', 'Strategy'],
    integrations: ['GA4', 'Meta Ads', 'Google Ads'],
    iconEmoji: '🔍',
  },
  {
    slug: 'data-question-answering',
    title: 'Ask Your Marketing Data Anything',
    description:
      'Plain-English questions, real answers from your connected data — no SQL, no dashboard hunting, no waiting for the analyst.',
    prompt:
      'Which landing page had the best conversion rate for paid traffic last month, and how did it differ between mobile and desktop?',
    category: 'Analytics & Reporting',
    tags: ['Analytics', 'Everyone'],
    integrations: ['GA4', 'Google Ads', 'Meta Ads'],
    iconEmoji: '💡',
  },

  // ── Automation & Monitoring ───────────────────────────────────────────
  {
    slug: 'budget-pacing-alerts',
    title: 'Daily Budget Pacing Watchdog',
    description:
      "Checks every account's spend against its monthly budget daily, projects month-end totals, and alerts you before anything overspends or stalls.",
    prompt:
      'Every morning, check spend pacing across all our accounts against monthly budgets. Alert me if any account is projected to overshoot or undershoot by more than 10%.',
    category: 'Automation & Monitoring',
    tags: ['Agency', 'Automation'],
    integrations: ['Google Ads', 'Meta Ads', 'Slack'],
    iconEmoji: '⏱️',
  },
  {
    slug: 'disapproved-ads-watcher',
    title: 'Disapproved Ads Instant Alert',
    description:
      "Watches all accounts for ad disapprovals and account flags, alerts you immediately, and drafts the compliant fix before you've read the email.",
    prompt:
      'Monitor all our ad accounts for disapprovals and policy flags. When something gets rejected, alert me immediately with the reason and a suggested compliant rewrite.',
    category: 'Automation & Monitoring',
    tags: ['Agency', 'Automation'],
    integrations: ['Google Ads', 'Meta Ads', 'Slack'],
    iconEmoji: '🚦',
  },
  {
    slug: 'competitor-ads-weekly-digest',
    title: 'Weekly Competitor Ads Digest',
    description:
      "Every week, checks your competitors' ad libraries for new creatives, angles, and offers — and digests what changed into a five-minute read.",
    prompt:
      'Every Friday, check what our top 5 competitors launched in their ad libraries this week — new creatives, new angles, new offers — and send me a digest.',
    category: 'Automation & Monitoring',
    tags: ['Strategy', 'Automation'],
    integrations: ['Meta Ads', 'Slack'],
    iconEmoji: '📬',
  },
  {
    slug: 'rank-tracking-alerts',
    title: 'Keyword Rank Movement Alerts',
    description:
      "Tracks your priority keywords daily and alerts you on meaningful movement — yours or a competitor's — with the likely cause attached.",
    prompt:
      "Track our top 30 keywords daily. Alert me when anything moves more than 3 positions — ours or a competitor's — and include what probably caused it.",
    category: 'Automation & Monitoring',
    tags: ['SEO', 'Automation'],
    integrations: ['Google Search Console', 'Slack'],
    iconEmoji: '📶',
  },
  {
    slug: 'spend-stall-detection',
    title: 'Detect Stalled Campaigns Before They Cost You',
    description:
      'Notices when a campaign stops spending or delivery collapses — payment issues, exhausted audiences, bid problems — and diagnoses it automatically.',
    prompt:
      "Watch our campaigns for delivery problems: if any campaign's spend stalls or drops sharply, diagnose whether it's payments, audience saturation, or bidding, and notify me with the fix.",
    category: 'Automation & Monitoring',
    tags: ['Growth', 'Automation'],
    integrations: ['Meta Ads', 'Google Ads', 'Slack'],
    iconEmoji: '🛑',
  },
  {
    slug: 'auto-pause-rules',
    title: 'Guardrail Rules That Pause Losers',
    description:
      'Enforces your performance guardrails around the clock — pause ads past a CPA threshold, cap frequency, stop spend on broken landing pages.',
    prompt:
      'Set up guardrails: pause any ad that spends $150 with no conversion, alert me at frequency above 5, and stop all traffic to any landing page that starts erroring.',
    category: 'Automation & Monitoring',
    tags: ['PPC', 'Automation'],
    integrations: ['Meta Ads', 'Google Ads'],
    iconEmoji: '🧯',
  },
  {
    slug: 'weekly-search-terms-sweep',
    title: 'Weekly Search Terms Sweep',
    description:
      'Runs the search terms hygiene loop weekly — new negatives added, new keyword opportunities surfaced — so waste never accumulates.',
    prompt:
      'Every week, sweep our Google Ads search terms: add obvious junk as negatives automatically, and send me anything ambiguous plus new keyword opportunities worth breaking out.',
    category: 'Automation & Monitoring',
    tags: ['PPC', 'Automation'],
    integrations: ['Google Ads', 'Slack'],
    iconEmoji: '🧹',
  },
  {
    slug: 'site-health-monitor',
    title: 'Weekly Site and SEO Health Check',
    description:
      'Crawls your site weekly for new broken links, indexing drops, page speed regressions, and Search Console errors — before they cost rankings.',
    prompt:
      'Run a weekly health check on our site: broken links, deindexed pages, Core Web Vitals regressions, and new Search Console errors. Only ping me when something needs action.',
    category: 'Automation & Monitoring',
    tags: ['SEO', 'Automation'],
    integrations: ['Google Search Console', 'Slack'],
    iconEmoji: '🏥',
  },
  {
    slug: 'brand-mention-tracking',
    title: 'Brand Mention and Review Tracker',
    description:
      'Watches the web for new mentions of your brand — reviews, forums, social, press — and routes what needs a response to the right person.',
    prompt:
      'Track mentions of our brand across reviews, Reddit, and social. Send a weekly digest, but alert me immediately for anything negative gaining traction.',
    category: 'Automation & Monitoring',
    tags: ['Brand', 'Automation'],
    integrations: ['Slack'],
    iconEmoji: '👂',
  },
  {
    slug: 'lead-routing-notification',
    title: 'Instant Lead Alerts With Context',
    description:
      'When a lead comes in, enriches it — company, role, which campaign brought them — and posts it where your team works with suggested next steps.',
    prompt:
      'When a new lead submits our form, enrich it with their company and role, note which campaign and ad brought them, and post it to our sales channel with a suggested first touch.',
    category: 'Automation & Monitoring',
    tags: ['Sales', 'B2B SaaS', 'Automation'],
    integrations: ['Slack', 'Lark'],
    iconEmoji: '🔔',
  },
  {
    slug: 'scheduled-creative-refresh',
    title: 'Scheduled Creative Refresh Pipeline',
    description:
      "On a fixed cadence, reviews creative fatigue, generates a new batch based on what's winning, and queues it for your approval — a standing pipeline.",
    prompt:
      "Every two weeks: review which creatives are fatiguing, generate a new batch based on the current winners' patterns, and queue them for my approval before launch.",
    category: 'Automation & Monitoring',
    tags: ['Creative strategist', 'Automation'],
    integrations: ['Meta Ads'],
    iconEmoji: '♻️',
  },
  {
    slug: 'turn-workflow-into-skill',
    title: 'Turn Any Workflow Into a Reusable Skill',
    description:
      'Take something Soku just did well — an audit, a report, a creative process — and save it as a named skill your whole team can rerun on demand.',
    prompt:
      'That competitive analysis you just ran was exactly what we need monthly. Turn it into a reusable skill so anyone on the team can run it for any client.',
    category: 'Automation & Monitoring',
    tags: ['Agency', 'Automation'],
    integrations: [],
    iconEmoji: '🧰',
  },
  {
    slug: 'monthly-account-audit-schedule',
    title: 'Standing Monthly Account Audit',
    description:
      'Runs the full account audit automatically every month and delivers the diff — what improved, what regressed, what needs attention now.',
    prompt:
      "Run our full ads account audit on the 1st of every month. Compare against last month's audit and highlight what improved, what regressed, and the top 3 actions.",
    category: 'Automation & Monitoring',
    tags: ['Agency', 'Automation'],
    integrations: ['Google Ads', 'Meta Ads'],
    iconEmoji: '🗓️',
  },
  {
    slug: 'backlink-monitor',
    title: 'Backlink Gained/Lost Monitor',
    description:
      'Watches your backlink profile for valuable links gained and lost — and alerts you when a lost link is worth a reclamation email.',
    prompt:
      'Monitor our backlinks weekly: notify me of new links from real sites and any lost links worth reclaiming, with a draft outreach email for the reclamations.',
    category: 'Automation & Monitoring',
    tags: ['SEO', 'Automation'],
    integrations: ['Slack'],
    iconEmoji: '⛓️',
  },
  {
    slug: 'autopilot-full-account',
    title: 'Put Routine Account Management on Autopilot',
    description:
      'Soku handles the routine loop — pacing, negatives, fatigue, alerts — on a standing schedule, and escalates to you only for judgment calls.',
    prompt:
      'Take over the routine management of our ad accounts: daily pacing checks, weekly search term hygiene, creative fatigue monitoring. Handle the routine, escalate the judgment calls to me.',
    category: 'Automation & Monitoring',
    tags: ['Founder', 'Automation'],
    integrations: ['Google Ads', 'Meta Ads', 'Slack'],
    iconEmoji: '🛩️',
  },

  // ── Landing Pages & CRO ───────────────────────────────────────────────
  {
    slug: 'landing-page-from-brief',
    title: 'Build a Landing Page From a Brief',
    description:
      'Designs and writes a complete landing page for a campaign — structure, copy, social proof placement, and a form tuned for conversion.',
    prompt:
      'Build a landing page for our webinar campaign: headline options, section structure, speaker credibility, and a registration form short enough that people finish it.',
    category: 'Landing Pages & CRO',
    tags: ['Growth', 'CRO'],
    integrations: [],
    iconEmoji: '🏗️',
  },
  {
    slug: 'landing-page-cro-audit',
    title: 'Landing Page Conversion Audit',
    description:
      'Audits your landing page against conversion principles — message clarity, friction, trust, speed — scored with the specific fixes ranked by lift.',
    prompt:
      'Audit our main landing page for conversion: message clarity, friction points, trust signals, load speed. Score it and rank the fixes by expected lift.',
    category: 'Landing Pages & CRO',
    tags: ['CRO'],
    integrations: ['GA4'],
    iconEmoji: '🔍',
  },
  {
    slug: 'ab-test-ideas-backlog',
    title: 'Build a Prioritized A/B Test Backlog',
    description:
      'Generates test hypotheses from your analytics data and page audits, then prioritizes them by impact, confidence, and effort into a real backlog.',
    prompt:
      'Look at our funnel data and landing pages, generate 15 A/B test hypotheses, and prioritize them by ICE score into a testing backlog for this quarter.',
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'Growth'],
    integrations: ['GA4', 'PostHog'],
    iconEmoji: '🧪',
  },
  {
    slug: 'message-match-landing-variants',
    title: 'Landing Page Variants Matched to Each Ad Angle',
    description:
      "Creates a landing page variant for each major ad angle so the page continues the ad's promise — the cheapest conversion lift there is.",
    prompt:
      "We run 4 ad angles into one generic page. Create a landing page variant matched to each angle — same structure, but headline and proof continuing each ad's specific promise.",
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'PPC'],
    integrations: [],
    iconEmoji: '🪞',
  },
  {
    slug: 'signup-flow-friction-analysis',
    title: 'Find Friction in Your Signup Flow',
    description:
      'Analyzes behavioral data through your signup or checkout flow, finds where users rage-click, stall, or abandon, and redesigns the problem steps.',
    prompt:
      'Analyze our signup flow in PostHog: where do users stall, rage-click, or drop? Diagnose the top 3 friction points and propose the redesign for each.',
    category: 'Landing Pages & CRO',
    tags: ['Product marketing', 'CRO'],
    integrations: ['PostHog'],
    iconEmoji: '🚧',
  },
  {
    slug: 'pricing-page-optimization',
    title: 'Pricing Page Teardown and Redesign',
    description:
      'Audits your pricing page against best patterns — anchoring, plan naming, FAQ objections — and redesigns it to move buyers to the right plan.',
    prompt:
      "Tear down our pricing page: anchoring, plan comparison clarity, objection handling. Redesign it to guide buyers to the mid-tier plan and reduce 'contact sales' dead-ends.",
    category: 'Landing Pages & CRO',
    tags: ['B2B SaaS', 'CRO'],
    integrations: [],
    iconEmoji: '💰',
  },
  {
    slug: 'form-optimization',
    title: 'Optimize Lead Forms for Completion',
    description:
      "Audits every form field against its analytics cost, cuts what isn't earning its friction, and restructures for completion without losing lead quality.",
    prompt:
      'Our demo request form has 9 fields. Analyze which fields cause abandonment, which we actually use in sales, and redesign it for completion without wrecking lead quality.',
    category: 'Landing Pages & CRO',
    tags: ['B2B SaaS', 'CRO'],
    integrations: ['GA4', 'PostHog'],
    iconEmoji: '📝',
  },
  {
    slug: 'social-proof-strategy',
    title: 'Place Social Proof Where It Converts',
    description:
      'Audits your proof assets — reviews, logos, numbers, case studies — and places the right proof at each objection point in the journey.',
    prompt:
      'Audit all our social proof assets and our funnel. Place the right proof at each objection point — which testimonial near pricing, which logos on the landing page, which numbers in ads.',
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'Brand'],
    integrations: [],
    iconEmoji: '🏅',
  },
  {
    slug: 'page-speed-conversion',
    title: 'Fix Page Speed Where It Costs Conversions',
    description:
      'Finds the pages where slow loading measurably costs you conversions, identifies the heaviest offenders, and fixes them in impact order.',
    prompt:
      'Which of our pages are slow enough to cost conversions? Cross page speed against bounce and conversion data, find the worst offenders on paid routes, and fix them.',
    category: 'Landing Pages & CRO',
    tags: ['Technical', 'CRO'],
    integrations: ['GA4'],
    iconEmoji: '⚡',
  },
  {
    slug: 'mobile-experience-audit',
    title: 'Mobile Experience Audit',
    description:
      'Walks your funnel on mobile like a real user — thumb reach, form usability, popup behavior — and fixes what desktop-first design broke.',
    prompt:
      'Audit our mobile experience end-to-end: landing page, navigation, forms, checkout. Most of our paid traffic is mobile — find everything that desktop-first design broke.',
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'Mobile'],
    integrations: ['GA4'],
    iconEmoji: '📱',
  },
  {
    slug: 'quiz-funnel-design',
    title: 'Design a Quiz Funnel',
    description:
      'Builds an interactive quiz that segments visitors, personalizes the recommendation, and captures the lead — with all questions and logic mapped.',
    prompt:
      'Design a quiz funnel for our skincare brand: questions that segment by skin type and concern, personalized product recommendations, and email capture before results.',
    category: 'Landing Pages & CRO',
    tags: ['Ecommerce', 'DTC'],
    integrations: [],
    iconEmoji: '❔',
  },
  {
    slug: 'thank-you-page-optimization',
    title: 'Make Thank-You Pages Do More Work',
    description:
      'Turns your dead-end confirmation pages into a next step — referral, upsell, content, calendar booking — matched to what the converter just did.',
    prompt:
      'Our thank-you pages are dead ends. Redesign each one with the right next step: booking a call after demo requests, referral after purchase, content after newsletter signup.',
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'Growth'],
    integrations: [],
    iconEmoji: '🙏',
  },
  {
    slug: 'exit-intent-strategy',
    title: "Exit-Intent Offers That Don't Annoy",
    description:
      'Designs exit-intent interventions matched to page context — the right save-offer on pricing vs blog vs checkout — without tanking your UX.',
    prompt:
      'Design exit-intent offers per page type: cart abandonment save on checkout, lead magnet on blog posts, discount-vs-demo test on pricing. Keep them non-annoying.',
    category: 'Landing Pages & CRO',
    tags: ['Ecommerce', 'CRO'],
    integrations: [],
    iconEmoji: '🚪',
  },
  {
    slug: 'personalize-landing-by-source',
    title: 'Personalize Pages by Traffic Source',
    description:
      "Adapts your landing page's headline and proof depending on where the visitor came from — LinkedIn sees the B2B story, TikTok sees the fun one.",
    prompt:
      "Set up source-based personalization on our homepage: visitors from LinkedIn ads see the enterprise story, from TikTok the creator angle, from Google search the exact query's promise.",
    category: 'Landing Pages & CRO',
    tags: ['CRO', 'Growth'],
    integrations: [],
    iconEmoji: '🎭',
  },
  {
    slug: 'checkout-abandonment-recovery',
    title: 'Diagnose and Recover Checkout Abandonment',
    description:
      'Finds why carts die at checkout — shipping shock, forced accounts, payment friction — fixes the flow, and builds the recovery email/SMS sequence.',
    prompt:
      'Diagnose our 70% checkout abandonment: is it shipping costs, forced account creation, or payment options? Fix the flow and set up the abandonment recovery sequence.',
    category: 'Landing Pages & CRO',
    tags: ['Ecommerce', 'Retention'],
    integrations: ['Shopify', 'GA4'],
    iconEmoji: '🛒',
  },
]

export const useCaseTemplates: UseCaseTemplate[] = [
  ...agentWorkflowUseCases,
  ...legacyUseCaseTemplates,
]

validateUseCaseTemplates(useCaseTemplates)
